// HelpDrawer — a "?" button for page headers that opens a right-side panel of
// per-page help copy. First of a standing convention: pages that need more
// explanation than a one-line subtitle mount one of these in their header and
// colocate the copy as children, right next to the page it explains.
//
// Deliberately simpler than Receiving/DrawerShell (that one is a centered
// modal with a full tab-trap for multi-field forms). This is read-only
// content, so the a11y bar is: Esc closes, focus lands in the panel on open
// and returns to the trigger button on close. No new deps.

import React, { useEffect, useRef, useState } from 'react';
import { IconButton } from './ui/Button';
import Tooltip from './ui/Tooltip';
import { IconClose } from './ui/icons';

interface HelpDrawerProps {
  title: string;
  children: React.ReactNode;
  /** aria-label on the trigger button. Defaults to "Help". */
  label?: string;
  /**
   * Replace the default "?" circle with your own trigger content.
   *
   * The circle is right for a page header, where it sits beside an <h1> and
   * everyone has learned what it means. It is wrong INSIDE a card, where a
   * second unlabeled "?" a few pixels from the field it explains reads as
   * decoration and gets ignored — there, a few words of link text is what
   * actually gets clicked. Only the trigger's appearance changes: the panel,
   * the Esc handling, the focus return and the tab trap are the same code, so a
   * second drawer implementation never has to exist to get a different button.
   */
  trigger?: React.ReactNode;
  /** className for the trigger button when `trigger` is supplied. */
  triggerClassName?: string;
}

const HelpDrawer: React.FC<HelpDrawerProps> = ({
  title,
  children,
  label = 'Help',
  trigger,
  triggerClassName,
}) => {
  const [open, setOpen] = useState(false);
  // Two-step mount so the transform transition actually animates: render off-
  // screen first, then flip to on-screen a tick later.
  const [visible, setVisible] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const raf = requestAnimationFrame(() => setVisible(true));
    closeRef.current?.focus();
    return () => cancelAnimationFrame(raf);
  }, [open]);

  const close = () => {
    setVisible(false);
    // Wait for the slide-out transition before unmounting, so it doesn't just
    // vanish. Matches the panel's 200ms transition below.
    setTimeout(() => setOpen(false), 200);
    triggerRef.current?.focus();
  };

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const tabbables = panelRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (tabbables.length === 0) return;
      const first = tabbables[0];
      const last = tabbables[tabbables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <>
      <span className="inline-flex">
        {trigger ? (
          <button
            ref={triggerRef}
            type="button"
            onClick={() => setOpen(true)}
            aria-label={label}
            className={triggerClassName}
          >
            {trigger}
          </button>
        ) : (
          <Tooltip label={label}>
          <IconButton
            ref={triggerRef}
            label={label}
            size="sm"
            onClick={() => setOpen(true)}
            className="text-xs font-bold"
          >
            ?
          </IconButton>
          </Tooltip>
        )}
      </span>

      {open && (
        <div
          className="fixed inset-0 z-[100]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="help-drawer-title"
        >
          <div
            className={`scrim transition-opacity duration-200 motion-reduce:transition-none ${
              visible ? 'opacity-100' : 'opacity-0'
            }`}
            onClick={close}
          />
          <div
            ref={panelRef}
            // An overlay above unpredictable content, so this is one of the
            // few places a shadow is right. No edge line: the shadow and the
            // tone step are the edge.
            className={`absolute inset-y-0 right-0 w-full max-w-md bg-surface-container-low shadow-2xl flex flex-col transition-transform duration-200 ease-emphasized motion-reduce:transition-none ${
              visible ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-outline-variant">
              <h2 id="help-drawer-title" className="text-base font-semibold text-primary">
                {title}
              </h2>
              <Tooltip label="Close">
              <IconButton ref={closeRef} label="Close" onClick={close}>
                <IconClose />
              </IconButton>
              </Tooltip>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 text-sm text-on-surface-variant leading-relaxed">
              {children}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HelpDrawer;
